/**
 * SwitchAndSave.tsx — the Switch & Save panel under the prior-treatment question.
 * Every word comes from `switchAndSaveCopy.ts`; this file only lays it out.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * The mark is drawn inline because the app is CSP-locked and has no image for
 * it. It is DECORATIVE (`aria-hidden`): the words beside it are the heading, so a
 * screen reader hears "Switch & Save" once, as the name of the region.
 * ⚠️ If marketing supplies an official logo it replaces `SwitchSaveMark`, and the
 * heading stays real text. A logo image as the only heading would leave the panel
 * unnamed for anyone who cannot see it.
 */
import { useId } from 'react';
import { KeepCompoundsWhole } from '../ui/KeepCompoundsWhole';
import { CheckIcon } from '../ui/kit';
import { SWITCH_SAVE_BENEFITS, SWITCH_SAVE_BENEFITS_INTRO, SWITCH_SAVE_LEAD } from './switchAndSaveCopy';

/** Two opposing arrows in a filled disc: the "swap" the heading names. */
function SwitchSaveMark() {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 40 40" width={40} height={40} style={{ flexShrink: 0 }}>
      <circle cx="20" cy="20" r="20" style={{ fill: 'var(--gps-primary)' }} />
      <path
        d="M12 16h15l-4-4M28 24H13l4 4"
        fill="none"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ stroke: 'var(--gps-on-accent)' }}
      />
    </svg>
  );
}

/**
 * `className` is the caller's layout hook — QuestionCard's side-column grid on
 * desktop (`gps-jux-sas-aside`, see tokens.css). The panel's own look stays here.
 */
export function SwitchAndSave({ className }: { className?: string } = {}) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={className}
      style={{
        marginTop: 'var(--gps-s-4)',
        padding: '18px 20px',
        background: 'var(--gps-primary-light)',
        border: '1px solid var(--gps-primary-tint-strong)',
        borderRadius: 'var(--gps-r-lg)',
        color: 'var(--gps-text)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <SwitchSaveMark />
        <h2
          id={headingId}
          style={{
            margin: 0,
            fontFamily: 'var(--gps-font-display)',
            fontSize: '22px',
            // Fraunces is bundled at 400 and 600 only; 700 would be synthesised.
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          Switch <span style={{ color: 'var(--gps-green-text)' }}>&amp;</span> Save
        </h2>
      </div>
      {/* "weight-loss" is kept whole here as on the question beside it (review,
          2026-09-25): the same words, only where a line may end. */}
      <p style={{ margin: '0 0 12px', fontSize: '15px', lineHeight: 1.5 }}>
        <KeepCompoundsWhole text={SWITCH_SAVE_LEAD} />
      </p>
      <p style={{ margin: '0 0 6px', fontSize: '14.5px', fontWeight: 600 }}>{SWITCH_SAVE_BENEFITS_INTRO}</p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '6px' }}>
        {SWITCH_SAVE_BENEFITS.map((benefit) => (
          <li key={benefit} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14.5px', lineHeight: 1.45 }}>
            <span style={{ color: 'var(--gps-green-text)', marginTop: '2px' }}>
              <CheckIcon size={14} />
            </span>
            <span>
              <KeepCompoundsWhole text={benefit} />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
